import React, { useState } from 'react';
import { Button, Card, List, Typography, Popconfirm, Divider, Modal, Input, Tag, message } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { Permissions } from '@whatsper/texterchat-common';
import { observer } from 'mobx-react';
import RevisionItem from '../../RevisionItem/RevisionItem';
import RevisionDetailView from '../../RevisionDetailView/RevisionDetailView';
import { useStore } from '../../../../store/rootStore';
import { buildScenarioExport } from '../../../../store/ScenariosStore/ScenariosStore';
import { saveAs } from 'file-saver';
import styles from '../../ScenariosManager.module.scss';
const ScenarioDetailView = ({ name, description, revisions, drafts, activeRevision, loadingActions, onCopyRevision, onNewDraft, onActivateRevision, onInactivateRevision, onDeleteRevision, onPromoteDraft, onDeleteDraft, onEditDraft, isSystemScenario, }) => {
    const { t } = useTranslation();
    const { agentsStore, loginStore } = useStore();
    const [isPromoteModalVisible, setIsPromoteModalVisible] = useState(false);
    const [promotionComment, setPromotionComment] = useState('');
    const [draftToPromote, setDraftToPromote] = useState(null);
    const [selectedRevision, setSelectedRevision] = useState(null);
    const activeRevisionVersion = activeRevision === null || activeRevision === void 0 ? void 0 : activeRevision.version;
    const allRevisions = activeRevision ? [...revisions, activeRevision] : revisions;
    const sortedRevisions = allRevisions.slice().sort((a, b) => b.version - a.version);
    const userDrafts = drafts.filter((d) => { var _a; return d.metadata.authorUid === ((_a = loginStore.userDetails) === null || _a === void 0 ? void 0 : _a._id); });
    const hasDraft = userDrafts.length > 0;
    const canActivate = agentsStore.hasPermissions([Permissions.Permission.scenarios_activate]);
    const canDelete = agentsStore.hasPermissions([Permissions.Permission.scenarios_delete]);
    const showPromoteModal = (draft) => {
        setDraftToPromote(draft);
        setPromotionComment('');
        setIsPromoteModalVisible(true);
    };
    const handlePromoteOk = () => {
        if (draftToPromote) {
            onPromoteDraft(draftToPromote, promotionComment);
            setIsPromoteModalVisible(false);
            setDraftToPromote(null);
            setPromotionComment('');
        }
    };
    const handlePromoteCancel = () => {
        setIsPromoteModalVisible(false);
        setDraftToPromote(null);
        setPromotionComment('');
    };
    const handleViewRevisionDetails = (revision) => {
        setSelectedRevision(revision);
    };
    const handleBackFromRevisionDetails = () => {
        setSelectedRevision(null);
    };
    const exportRevision = (revision) => {
        try {
            const data = buildScenarioExport(revision);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
            const fileName = `${revision.name || 'scenario'}_v${revision.version}.json`;
            saveAs(blob, fileName);
        }
        catch (e) {
            message.error(React.createElement(Trans, { i18nKey: "scenarioExportError", defaults: "Failed to export scenario" }));
        }
    };
    const exportDraft = (draft) => {
        try {
            const data = buildScenarioExport(draft);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
            const suffix = draft.parentVersion ? `_draft_from_v${draft.parentVersion}` : '_draft';
            const fileName = `${draft.name || 'scenario'}${suffix}.json`;
            saveAs(blob, fileName);
        }
        catch (e) {
            message.error(React.createElement(Trans, { i18nKey: "draftExportError", defaults: "Failed to export draft" }));
        }
    };
    // If a revision is selected for viewing details, show the revision detail view
    if (selectedRevision) {
        return (React.createElement(RevisionDetailView, { revision: selectedRevision, onBack: handleBackFromRevisionDetails }));
    }
    return (React.createElement("div", null,
        React.createElement(Typography.Title, { level: 4, className: styles.scenarioTitle },
            React.createElement("span", null, name),
            isSystemScenario && (React.createElement(Tag, { color: "blue", className: styles.systemBadge },
                React.createElement(Trans, { i18nKey: "systemScenarioBadge", defaults: "System" })))),
        React.createElement(Typography.Paragraph, null, description),
        React.createElement(Divider, null),
        userDrafts.length > 0 && (React.createElement(Card, { title: React.createElement(Trans, { i18nKey: "scenarioDraft", defaults: "Draft" }), style: { marginBottom: 24 } },
            React.createElement(List, { itemLayout: "horizontal", dataSource: userDrafts, renderItem: (draft) => (React.createElement(List.Item, { actions: [
                        !isSystemScenario && (React.createElement(Button, { type: "primary", onClick: () => onEditDraft(draft) },
                            React.createElement(Trans, { i18nKey: "edit", defaults: "Edit" }))),
                        React.createElement(Button, { onClick: () => exportDraft(draft) },
                            React.createElement(Trans, { i18nKey: "export", defaults: "Export" })),
                        !isSystemScenario && (React.createElement(Button, { onClick: () => showPromoteModal(draft), loading: loadingActions.has(`promote-${draft._id}`), disabled: !draft.actions || draft.actions.length === 0 },
                            React.createElement(Trans, { i18nKey: "promoteToRevision" }))),
                        !isSystemScenario && (React.createElement(Popconfirm, { title: React.createElement(Trans, { i18nKey: "discardDraftConfirm", defaults: "Are you sure you want to discard this draft?" }), onConfirm: () => onDeleteDraft(draft) },
                            React.createElement(Button, { danger: true, loading: loadingActions.has(`delete-draft-${draft._id}`) },
                                React.createElement(Trans, { i18nKey: "discard", defaults: "Discard" })))),
                    ].filter(Boolean) },
                    React.createElement(List.Item.Meta, { title: draft.parentVersion ? (React.createElement(Trans, { i18nKey: "scenarioDraftBasedOnRevision", defaults: "Draft based on Revision {{version}}", values: { version: draft.parentVersion } })) : (React.createElement(Trans, { i18nKey: "scenarioDraft", defaults: "Draft" })) }))) }))),
        React.createElement(Card, { title: React.createElement(Trans, { i18nKey: "revisions", defaults: "Revisions" }) },
            React.createElement(List, { itemLayout: "vertical", dataSource: sortedRevisions, renderItem: (revision) => (React.createElement(RevisionItem, { key: revision.version, revision: revision, isActive: revision.version === activeRevisionVersion, hasDraft: hasDraft, onCopy: onCopyRevision, onExport: exportRevision, onNewDraft: onNewDraft, onActivate: onActivateRevision, onDelete: onDeleteRevision, onViewDetails: handleViewRevisionDetails, canActivate: canActivate, canDelete: canDelete, loadingNewDraft: loadingActions.has(`new-draft-${revision.version}`), loadingActivate: loadingActions.has(`activate-${revision.version}`), loadingDelete: loadingActions.has(`delete-revision-${revision.version}`), isSystemScenario: isSystemScenario })) })),
        activeRevision && !isSystemScenario && (React.createElement(Popconfirm, { title: React.createElement(Trans, { i18nKey: "inactivateScenarioConfirm", defaults: "Inactivate this scenario?" }), onConfirm: onInactivateRevision },
            React.createElement(Button, { danger: true, loading: loadingActions.has('inactivate'), style: { marginTop: 16 } },
                React.createElement(Trans, { i18nKey: "inactivateScenario" })))),
        React.createElement(Modal, { title: React.createElement(Trans, { i18nKey: "promoteDraftTitle", defaults: "Promote Draft to Revision" }), open: isPromoteModalVisible, onOk: handlePromoteOk, onCancel: handlePromoteCancel, okText: React.createElement(Trans, { i18nKey: "promote", defaults: "Promote" }), cancelText: React.createElement(Trans, { i18nKey: "cancel", defaults: "Cancel" }) },
            React.createElement(Typography.Paragraph, null,
                React.createElement(Trans, { i18nKey: "confirmPromoteDraft" })),
            React.createElement(Input.TextArea, { placeholder: t('revisionCommentPlaceholder', 'Optional comment for this revision'), value: promotionComment, onChange: (e) => setPromotionComment(e.target.value), rows: 3 }))));
};
export default observer(ScenarioDetailView);
